//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import {
  BodySecondaryText,
  BodyText,
  MetatagText,
  SubsectionText,
} from '@components/atoms/text/Text';
import {
  DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  TablePaginationControls,
} from '@components/molecules/pagination/TablePaginationControls';
import { UiaProductItem } from '@uia/products/UiaProductItem';
import {
  MAX_NUM_CATALOG_SUGGESTIONS,
  ProductResultsTypes,
  uiaProductPrefix,
} from '@uia/products/constants/UiaProductConstants';
import { isEmptyValue } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

export interface UiaProductGridProps {
  handlePageChange: (currentPage: number, itemsPerPage: number) => void;
  isLoadingProducts: boolean;
  limit: number;
  offset: number;
  productsResults: CuralateCatalogProduct[] | undefined;
  searchQuery: string;
  tagSingleProduct: (product: CuralateCatalogProduct) => void;
}

export const UiaProductGrid = ({
  handlePageChange,
  isLoadingProducts,
  limit,
  offset,
  productsResults,
  searchQuery,
  tagSingleProduct,
}: UiaProductGridProps) => {
  const theme = useTheme();
  const [selectedProducts, setSelectedProducts] = useState<
    CuralateCatalogProduct[]
  >([]);

  const products = productsResults ?? [];
  // TODO: PD-284075 - get product result type
  const productResultsType: ProductResultsTypes =
    ProductResultsTypes.CATALOG_SUGGESTIONS;

  const isCatalogSuggestions =
    productResultsType === ProductResultsTypes.CATALOG_SUGGESTIONS;

  const isCatalogResults = false;
  // TODO: PD-284075 - uncomment below when we get productResultsType.
  // Currently setting it to false to avoid this error: 'ProductResultsTypes.CATALOG_SUGGESTIONS' and 'ProductResultsTypes.CATALOG_RESULTS' have no overlap
  // const isCatalogResults =
  //   productResultsType === ProductResultsTypes.CATALOG_RESULTS;

  const selectProduct = (product: CuralateCatalogProduct) => {
    if (
      !selectedProducts.find(
        (item) => item.productStyleId === product.productStyleId,
      )
    ) {
      setSelectedProducts([...selectedProducts, product]);
    } else {
      setSelectedProducts(
        selectedProducts.filter(
          (item) => item.productStyleId !== product.productStyleId,
        ),
      );
    }
  };

  return isLoadingProducts ? (
    <CenteredSpinner />
  ) : (
    <>
      {products.length > 0 && (
        <div>
          {isCatalogSuggestions && (
            <CatalogSuggestionsWrapper>
              <CatalogSuggestionsText>
                <MetatagText>Detected Products</MetatagText>
                <BodySecondaryText>
                  {Math.min(MAX_NUM_CATALOG_SUGGESTIONS, products.length)} Items
                </BodySecondaryText>
              </CatalogSuggestionsText>
            </CatalogSuggestionsWrapper>
          )}
          {isCatalogResults && (
            // TODO: PD-285354 - change to totalCount
            <BodyText>{`${pluralize(products.length, 'product')} found`}</BodyText>
          )}

          <ProductGrid>
            {products.map((product) => (
              <UiaProductItem
                key={product.destinationUrl}
                isSelected={selectedProducts.some(
                  (item) => item.productStyleId === product.productStyleId,
                )}
                product={product}
                resultsType={productResultsType}
                selectProduct={() => selectProduct(product)}
                tagSingleProduct={tagSingleProduct}
              />
            ))}
          </ProductGrid>
          <TablePaginationControlsWrapper>
            <TablePaginationControls
              currentPage={offset / limit}
              dropdownContentZIndex={theme.zindex.popover} // To ensure pagination dropdown select appears on top of the UIA modal
              handlePageChange={handlePageChange}
              itemsPerPage={limit}
              itemsPerPageOptions={DEFAULT_ITEMS_PER_PAGE_OPTIONS}
              numItems={products.length} // TODO: PD-285354 - change to totalCount
              trackingIdPrefix={uiaProductPrefix}
            />
          </TablePaginationControlsWrapper>
        </div>
      )}
      {products.length === 0 && !isEmptyValue(searchQuery) && (
        <NoResultsContainer>
          <SubsectionText>No Products Found</SubsectionText>
          <BodyText>Change your search term and try again.</BodyText>
        </NoResultsContainer>
      )}
    </>
  );
};

const ProductGrid = styled.div(
  ({ theme }) => `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[3]} 0;
  `,
);

const CatalogSuggestionsText = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
  `,
);

const CatalogSuggestionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const NoResultsContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: ${theme.spacings[3]};
  `,
);

const TablePaginationControlsWrapper = styled.div(
  ({ theme }) => `
    margin-top: ${theme.spacings[3]};
  `,
);
